import { Spinner } from '@Common/Spinner';
import { Listbox } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { AddressElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeError } from '@stripe/stripe-js';
import {
  ALL_TAX_TYPES,
  defaultToastProps,
  EU_COUNTRIES,
  TAX_TYPE_BY_COUNTRY,
} from 'appConstants';
import { classNames, isEqual } from 'helpers/validation';
import { FormEvent, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { useGetSubscription, useUpdateBillingInfo } from 'store/subscription/actions';
import { BillingInfoRequestSchema } from 'store/subscription/types';

interface BillingFormProps {
  onSubmit: () => void;
  submitText?: string;
}

export const BillingForm = ({ onSubmit, submitText = 'next' }: BillingFormProps) => {
  const intl = useIntl();
  const { data: subscription } = useGetSubscription();
  const { mutate: onUpdateBilling, isPending } = useUpdateBillingInfo();
  const [taxId, setTaxId] = useState('');
  const [taxType, setTaxType] = useState(ALL_TAX_TYPES[0]);
  const [country, setCountry] = useState<string | null>(
    subscription?.address?.country || null,
  );
  const stripe = useStripe();
  const elements = useElements();

  const [loading, setLoading] = useState(false);

  const handleError = (error: StripeError) => {
    setLoading(false);
    toast(error.message, {
      ...defaultToastProps,
      type: 'error',
    });
  };

  useEffect(() => {
    if (country) {
      setTaxType(TAX_TYPE_BY_COUNTRY[country as keyof typeof TAX_TYPE_BY_COUNTRY][0]);
    }
  }, [country]);

  useEffect(() => {
    if (subscription) {
      if (subscription.address?.country) {
        setCountry(subscription.address.country);
        const countryTax =
          TAX_TYPE_BY_COUNTRY[country as keyof typeof TAX_TYPE_BY_COUNTRY];

        if (subscription.tax_type) {
          const activeTaxType = countryTax.find(
            (tax) => tax.value === subscription.tax_type,
          );
          setTaxType(activeTaxType || countryTax[0]);
        }
        if (subscription.tax_value) {
          setTaxId(subscription.tax_value);
        }
      }
    }
  }, [subscription]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setLoading(true);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      handleError(submitError);
      return;
    }

    const addressElement = elements.getElement('address');

    if (addressElement) {
      const value = await addressElement.getValue();

      if (value.complete && taxId.trim() && taxType) {
        if (
          subscription?.address &&
          isEqual(value.value.address, subscription.address) &&
          taxType.value === subscription?.tax_type &&
          taxId === subscription?.tax_value &&
          value.value.name === subscription.name
        ) {
          onSubmit();
        } else {
          onUpdateBilling(
            {
              ...value.value.address,
              tax_type: taxType.value,
              value: taxId,
              company_name: value.value.name,
            } as BillingInfoRequestSchema,
            {
              onSuccess: onSubmit,
            },
          );
        }
      } else {
        toast(intl.formatMessage({ id: 'fillAllRequireFields' }), {
          ...defaultToastProps,
          type: 'error',
        });
      }
    }

    setLoading(false);
  };

  const taxTypeOptions = country
    ? TAX_TYPE_BY_COUNTRY[country as keyof typeof TAX_TYPE_BY_COUNTRY]
    : ALL_TAX_TYPES;

  return (
    <form onSubmit={handleSubmit}>
      <AddressElement
        options={{
          mode: 'billing',
          allowedCountries: EU_COUNTRIES,
          display: {
            name: 'organization',
          },
          defaultValues: {
            name: subscription?.name,
            address: subscription?.address
              ? subscription?.address
              : {
                  country: 'AT',
                },
          },
        }}
        onChange={async () => {
          const addressValue = await elements?.getElement('address')?.getValue();

          if (addressValue) {
            setCountry(addressValue.value.address.country);
          }
        }}
      />

      <div className="flex items-center justify-between w-full mt-2 gap-3">
        <Listbox value={taxType} onChange={setTaxType} as="div" className="w-1/2">
          <Listbox.Label className="block text-sm font-medium leading-6 text-gray-900">
            <FormattedMessage id="taxType" />
          </Listbox.Label>
          <div className="relative mt-2">
            <Listbox.Button className="relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6">
              <span className="block truncate">{taxType.label}</span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronUpDownIcon aria-hidden="true" className="h-5 w-5 text-gray-400" />
              </span>
            </Listbox.Button>

            <Listbox.Options className="absolute z-10 mt-1 max-h-44 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:text-sm">
              {taxTypeOptions.map((type) => (
                <Listbox.Option
                  key={type.value}
                  value={type}
                  className={({ active }) =>
                    classNames(
                      active ? 'bg-indigo-600 text-white' : 'text-gray-900',
                      'group relative cursor-default select-none py-2 pl-8 pr-4',
                    )
                  }
                >
                  {({ selected, active }) => (
                    <>
                      <span
                        className={classNames(
                          selected ? 'font-semibold' : 'font-normal',
                          'block truncate',
                        )}
                      >
                        {type.label}
                      </span>

                      {selected ? (
                        <span
                          className={classNames(
                            active ? 'text-white' : 'text-indigo-600',
                            'absolute inset-y-0 left-0 flex items-center pl-1.5',
                          )}
                        >
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      ) : null}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </div>
        </Listbox>
        <div className="w-1/2">
          <label
            htmlFor="taxId"
            className="block text-sm font-medium leading-6 text-gray-900"
          >
            <FormattedMessage id="taxId" />
          </label>
          <div className="mt-2">
            <input
              id="taxId"
              name="taxId"
              type="text"
              value={taxId}
              onChange={(e) => setTaxId(e.target.value)}
              placeholder={intl.formatMessage({ id: 'yourTaxId' })}
              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
          </div>
        </div>
      </div>

      <div className="w-full flex items-center justify-end mt-4">
        <button
          disabled={!stripe || loading || isPending}
          type="submit"
          className="rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          {loading || isPending ? (
            <Spinner color="bg-white" />
          ) : (
            <FormattedMessage id={submitText} />
          )}
        </button>
      </div>
    </form>
  );
};
